import React, { useEffect, useMemo } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import type { AuditoriaItem } from '../../types/auditoria';
import { extrairDiff } from '../../utils/corAuditoria';

const fmtValor = (v: unknown): string => (v === null || v === undefined ? '—' : String(v));

export const DialogoEvento: React.FC<{ item: AuditoriaItem; onFechar: () => void }> = ({ item, onFechar }) => {
  // Tenta formatar o JSON; se falhar, mostra a string crua.
  const dadosFormatados = useMemo(() => {
    if (!item.dados) return '(sem payload)';
    try {
      return JSON.stringify(JSON.parse(item.dados), null, 2);
    } catch {
      return item.dados;
    }
  }, [item.dados]);

  const linhasDiff = useMemo(() => (item.dados ? extrairDiff(item.dados) : null), [item.dados]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onFechar();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onFechar]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-[var(--scrim)] p-4"
      onClick={onFechar}
    >
      <div
        className="flex max-h-[80vh] w-full max-w-3xl flex-col gap-3 rounded-iris-lg bg-surface border border-line p-4 shadow-iris-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-2">
          <div>
            <h2 className="text-sm font-bold text-tx">{item.tipo}</h2>
            <p className="mt-0.5 text-xs text-tx-2">
              {item.usuario ?? 'sem usuário'} &middot; {new Date(item.criadoEm).toLocaleString('pt-BR')}
              {item.correlationId && (
                <> &middot; <span className="font-mono">correlation: {item.correlationId}</span></>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onFechar}
            className="rounded-iris-sm p-1 text-tx-3 hover:bg-surface-2"
            aria-label="Fechar"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>

        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          {item.entidade && (
            <>
              <dt className="text-tx-3">Entidade</dt>
              <dd className="font-mono text-tx">{item.entidade}</dd>
            </>
          )}
          {item.entidadeId && (
            <>
              <dt className="text-tx-3">ID</dt>
              <dd className="font-mono text-tx">{item.entidadeId}</dd>
            </>
          )}
          {item.ip && (
            <>
              <dt className="text-tx-3">IP</dt>
              <dd className="font-mono text-tx">{item.ip}</dd>
            </>
          )}
        </dl>

        {linhasDiff && (
          <div className="rounded-iris border border-line">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-line bg-surface-2 text-[11px] uppercase tracking-wide text-tx-3">
                  <th className="px-3 py-1.5 text-left">Campo</th>
                  <th className="px-3 py-1.5 text-left">De</th>
                  <th className="px-3 py-1.5 text-left">Para</th>
                </tr>
              </thead>
              <tbody>
                {linhasDiff.map(l => (
                  <tr key={l.campo} className="border-b border-line last:border-0">
                    <td className="px-3 py-1.5 font-medium text-tx">{l.campo}</td>
                    <td className="px-3 py-1.5 text-tx-3 line-through">{fmtValor(l.de)}</td>
                    <td className="px-3 py-1.5 text-tx">{fmtValor(l.para)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex-1 overflow-auto rounded-iris border border-line bg-surface-2 p-3">
          <pre className="whitespace-pre-wrap break-words font-mono text-[11px] text-tx">
            {dadosFormatados}
          </pre>
        </div>
      </div>
    </div>
  );
};

export default DialogoEvento;
